import React, {useContext} from 'react';
import PropTypes from 'prop-types';
import {DefaultPageTemplate} from '@tkww/orion-web-ds-page-layout';
import Navigation, {
  SHOP_DESTINATION_NAV_OVERRIDES,
} from '@tkww/reg-shared-ui-navigation';
import Cookies from 'js-cookie';
import {productsSearched} from '../../analytics';
import FillScreen from './FillScreen';
import Footer from '../Footer';
import HeaderNavWithMembershipModal from './HeaderNavWithMembershipModal';
import trackSegmentEvent from '../../utils/trackSegmentEvent';
import MemberContext from '../../contexts/MemberContext';
import useShopByInterestExperiment from '../../hooks/useShopByInterestExperiment';
import useOverviewToShopStagesAssignment from '../../hooks/useOverviewToShopStagesAssignment';
import {
  EXPERIMENT_CONFIGS,
  EXPERIMENT_SHOP_BY_INTEREST,
} from '../../constants/experiments';
import styles from './index.scss';

const ANALYTICS_APP_NAME = 'shopping';
const ANALYTICS_PRODUCT_NAME = 'shopping';
const REGISTRY_NAVIGATION_INTERACTION_EVENT_NAME =
  'Registry Navigation Interaction';
const NON_PLP_NAVIGATION_ITEMS = ['Add Store Registries', 'Cash Funds'];

const trackRegistryNavigationInteraction = (
  category,
  additionalEventFields = {},
) => {
  const {plpType, ...rest} = additionalEventFields;
  const isNonPlpNavigationItem = NON_PLP_NAVIGATION_ITEMS.includes(category);
  const hasPlpType = !isNonPlpNavigationItem && plpType;

  trackSegmentEvent({
    eventName: REGISTRY_NAVIGATION_INTERACTION_EVENT_NAME,
    source: 'subnav',
    categoryName: category || null,
    plpType: !hasPlpType ? null : plpType,
    ...rest,
  });
};

const onMenuItemHover = ({category}) => {
  if (category !== 'Collections') return;

  trackRegistryNavigationInteraction(category, {
    selection: 'view collections dropdown',
    product: 'catalog',
    plpType: null,
  });
};

const onMenuItemClick = ({category, plpType}) => {
  trackRegistryNavigationInteraction(category, {
    selection: 'subnav click',
    product: 'tkr store catalog',
    plpType,
  });
};

const AppLayout = ({children}) => {
  const {member, isFetchingMember} = useContext(MemberContext);
  const hasMemberLoaded = !isFetchingMember && !!member?.id;
  const isLoggedIn = Cookies.get('xo-session-token');

  const {variant, experimentId, isExperimentLoading} =
    useShopByInterestExperiment({
      experimentName: EXPERIMENT_SHOP_BY_INTEREST,
      isFeatureReady: hasMemberLoaded,
    }) || {};

  const {
    isInVariant: isShopStagesVariant,
    isExperimentLoading: isShopStagesLoading,
  } = useOverviewToShopStagesAssignment();

  // Shop experience (PDA-2092): the variant drops Discover/Checklist from the
  // black nav's Your Registry submenu and renames Overview to Shop. While the
  // read-only assignment resolves, the classic submenu renders.
  const isShopExperience = !isShopStagesLoading && !!isShopStagesVariant;

  const onSearchSubmit = ({searchTerm}) => {
    trackSegmentEvent({
      eventName: REGISTRY_NAVIGATION_INTERACTION_EVENT_NAME,
      selection: 'view search result',
      children: null,
      href: window.location.pathname,
      searchTerm,
      plpType: 'search results',
    });
    productsSearched({
      query: searchTerm,
    });
  };

  return (
    <DefaultPageTemplate
      className={styles.container}
      header={
        <HeaderNavWithMembershipModal
          analyticsProps={{product: ANALYTICS_PRODUCT_NAME}}
          metadata={{applicationName: ANALYTICS_APP_NAME}}
          activeProduct={null}
        />
      }
      footer={<Footer />}>
      <FillScreen>
        <Navigation
          memberId={member?.id}
          onSearchSubmit={onSearchSubmit}
          onMenuItemHover={onMenuItemHover}
          onMenuItemClick={onMenuItemClick}
          subMenuChildrenItemsStatus={{discover: true}}
          subMenuItemOverrides={
            isShopExperience ? SHOP_DESTINATION_NAV_OVERRIDES : undefined
          }
          applicationName="shopping"
          useCollectionsConceptualGrouping
          experiments={{
            [experimentId]: {
              assignment: !isLoggedIn
                ? EXPERIMENT_CONFIGS[EXPERIMENT_SHOP_BY_INTEREST][
                    process.env.ENV
                  ].variants.control.id
                : variant,
              isLoading: isExperimentLoading,
            },
          }}
        />
        {children}
      </FillScreen>
    </DefaultPageTemplate>
  );
};

AppLayout.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.element),
    PropTypes.element,
  ]).isRequired,
};

export default AppLayout;
